(ns superadmin.roles.ocp.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            [clojure.string :as str]
            [common.modal.view :refer [modal-v2 defmodal]]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-mutation]]
            [re-frame.core :as rf]
            [superadmin.roles.queries :as q]
            [uix.core :as uix :refer [$ defui]]))

(defui permissions-section [{:keys [permissions]}]
  ($ Stack {:spacing 1}
     ($ Typography {:variant "h6"}
        "Permissions")
     (if (empty? permissions)
       ($ Typography {:variant "body2" :color "text.secondary"}
          "No permissions")
       ($ Box {:sx #js {:p 2
                        :backgroundColor "grey.80"
                        :borderRadius 1}}
          ($ Stack {:spacing 1.5}
             (for [[resource actions] (sort-by first permissions)]
               ($ Box {:key resource
                       :sx #js {:pl 1.5
                                :py 0.5
                                :borderLeft "2px solid"
                                :borderColor "grey.60"}}
                  ($ Stack {:spacing 0.5}
                     ($ Typography {:variant "subtitle2" :sx #js {:fontWeight 600}}
                        (str/capitalize (name resource)))
                     ($ Stack {:spacing 0.25 :pl 1}
                        (for [[action status] (sort-by first actions)]
                          ($ Typography {:key action :variant "body2"}
                             (let [action-name (str/capitalize (name action))
                                   display-text (if (= status "allowed")
                                                  action-name
                                                  (str action-name " (" status ")"))]
                               display-text))))))))))))

(defui confirm-delete-role-modal [{:keys [role-name delete-fn]}]
  (let [[typed-name set-typed-name] (uix/use-state "")
        name-matches? (= typed-name role-name)]
    ($ modal-v2
       {:title "Delete role"
        :icon ($ SvgIcon {:color "error"}
                 ($ AlertIcon))
        :footer ($ Stack {:direction "row" :gap 1}
                   ($ Button {:variant "text"
                              :sx #js {:color "text.primary"}
                              :onClick #(rf/dispatch [:modal/close])}
                      "Cancel")
                   ($ Button {:variant "contained"
                              :color "error"
                              :disabled (not name-matches?)
                              :onClick #(do (rf/dispatch [:modal/close])
                                            (delete-fn))}
                      "Delete role"))}
       ($ Stack {:sx #js {:width "450px"}}
          ($ Typography {:variant "body1" :sx #js {:mb 2}}
             (str "Are you sure you want to delete the role '" role-name "'? This action cannot be undone."))
          ($ Typography {:variant "body2" :sx #js {:mb 1}}
             "Please type the role name to confirm:")
          ($ TextField
             {:fullWidth true
              :value typed-name
              :onChange #(set-typed-name (-> % .-target .-value))
              :placeholder role-name
              :data-testid "delete-role-confirmation-input"})))))

(defmodal confirm-delete-role ::confirm-delete-role
  [props]
  ($ confirm-delete-role-modal props))

(defui ocp-footer [{:keys [role]}]
  (let [role-id (:id role)
        {delete-role :mutate} (use-mutation (q/delete-role-mutation {:role-id role-id}))
        handle-delete (fn []
                        (delete-role))]
    ($ Stack {:direction "row"
              :justifyContent "flex-end"
              :spacing 1}
       ($ Button
          {:variant "outlined"
           :color "error"
           :onClick (fn []
                      (rf/dispatch [:modal/open {:id ::confirm-delete-role
                                                 :props {:role-id role-id
                                                         :role-name (:name role)
                                                         :delete-fn handle-delete}}]))
           :data-testid "delete-role-button"}
          "Delete")
       ($ Button
          {:variant "contained"
           :onClick (fn []
                      (rf/dispatch [:ocp/close])
                      (rf/dispatch [:ocp/open {:id :role/create-edit
                                               :props {:role
                                                       (cond-> role
                                                         (-> role :allowed-scopes empty?)
                                                         (assoc :superadmin-scope true))}}]))
           :data-testid "edit-role-button"}
          "Edit"))))

(defocp role-detail-ocp :role/detail
  {:width "50%"}
  [props]
  (let [{:keys [role]} props
        {:keys [id permissions allowed-scopes created-at updated-at] :as role-data} role
        role-name (:name role-data)]
    ($ ocp
       {:title (str "Role: " (or role-name "Details"))
        :footer ($ ocp-footer {:role role})}
       ($ Stack {:spacing 3}
          ;; Basic Info
          ($ Stack {:spacing 1}
             ($ Typography {:variant "h6"}
                "Basic Information")
             ($ Stack {:spacing 0.75}
                ($ Stack {:direction "row" :spacing 1}
                   ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "140px"}}
                      "ID:")
                   ($ Typography {:variant "body2"}
                      (str id)))
                ($ Stack {:direction "row" :spacing 1}
                   ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "140px"}}
                      "Name:")
                   ($ Typography {:variant "body2"}
                      role-name))
                ($ Stack {:direction "row" :spacing 1}
                   ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "140px"}}
                      "Allowed Scopes:")
                   ($ Stack {:direction "row" :spacing 0.5 :flexWrap "wrap"}
                      (if (seq allowed-scopes)
                        (for [scope allowed-scopes]
                          ($ Chip {:key scope
                                   :label (str scope)
                                   :size "small"
                                   :variant "outlined"
                                   :color "primary"}))
                        ($ Typography {:variant "body2" :color "text.secondary"}
                           "Superadmin"))))
                (when created-at
                  ($ Stack {:direction "row" :spacing 1}
                     ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "140px"}}
                        "Created:")
                     ($ Typography {:variant "body2"}
                        (-> created-at js/Date. str))))
                (when updated-at
                  ($ Stack {:direction "row" :spacing 1}
                     ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "140px"}}
                        "Updated:")
                     ($ Typography {:variant "body2"}
                        (-> updated-at js/Date. str))))))

          ($ permissions-section {:permissions permissions})))))